import i18n from "../../i18n";


// i18n language code -> Intl locale. en-GB gives day-first "DD MMM YYYY".
const LOCALE_MAP = {
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
  ch: "zh-CN",
};

const getLocale = () => LOCALE_MAP[(i18n.language || "").split("-")[0]] || "en-GB";

const pad2 = (n) => String(n).padStart(2, "0");

// Returns null for empty/invalid input.
const parseParts = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return {
      y: value.getFullYear(),
      mo: value.getMonth() + 1,
      d: value.getDate(),
      h: value.getHours(),
      mi: value.getMinutes(),
    };
  }

  const s = String(value).trim();
  if (!s) return null;

  // ISO-like: 2025-12-05 optionally followed by T/space HH:mm(:ss).
  // Day is optional so month-year values ("2025-12") also parse (day -> 1).
  let m = s.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?(?:[T ](\d{2}):(\d{2}))?/);
  if (m) {
    return {
      y: +m[1],
      mo: +m[2],
      d: m[3] != null ? +m[3] : 1,
      h: m[4] != null ? +m[4] : null,
      mi: m[5] != null ? +m[5] : null,
    };
  }

  // dd.mm.yyyy (tax rows)
  m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) {
    return { y: +m[3], mo: +m[2], d: +m[1], h: null, mi: null };
  }

  // Last resort for unknown formats (may reflect local timezone).
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    return {
      y: dt.getFullYear(),
      mo: dt.getMonth() + 1,
      d: dt.getDate(),
      h: dt.getHours(),
      mi: dt.getMinutes(),
    };
  }

  return null;
};

export const formatDate = (value) => {
  if (value == null || value === "") return "-";
  const p = parseParts(value);
  if (!p) return String(value).trim();
  const locale = getLocale();
  
  const dt = new Date(Date.UTC(p.y, p.mo - 1, p.d));
  // English: assemble day-first with the US short month ("11 Sep 2026").
  // en-GB renders September as "Sept"; the app spec uses "Sep".
  if (locale.startsWith("en")) {
    const mon = dt.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    return `${pad2(p.d)} ${mon} ${p.y}`;
  }
  // Chinese: assemble day-first ("24 七月 2026") — the native form is
  // year-first ("2026年7月24日"), but the app spec wants day-month-year.
  // month:"long" gives the Chinese text month ("七月"); "short" would give numeric "7月".
  if (locale.startsWith("zh")) {
    const mon = dt.toLocaleDateString(locale, { month: "long", timeZone: "UTC" });
    return `${pad2(p.d)} ${mon} ${p.y}`;
  }
  // Other locales: native localized form (correct punctuation, e.g. "11. Sept. 2026").
  return dt.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

// "14:30" (24-hour). No time part -> "-".
export const formatTime = (value) => {
  const p = parseParts(value);
  if (!p || p.h == null || p.mi == null) return "-";
  return `${pad2(p.h)}:${pad2(p.mi)}`;
};

// "11 Sep 2026, 14:30" (date always; time appended only when present).
// Empty -> "-"; unparseable non-empty -> original (no regression).
export const formatDateTime = (value) => {
  if (value == null || value === "") return "-";
  const p = parseParts(value);
  if (!p) return String(value).trim();
  const date = formatDate(value);
  if (p.h == null || p.mi == null) return date;
  return ` ${pad2(p.h)}:${pad2(p.mi)}, ${date}`;
};

// "Sep 2026" (localized month + year). Empty/invalid -> "-".
export const formatMonthYear = (value) => {
  const p = parseParts(value);
  if (!p) return "-";
  const locale = getLocale();
  const dt = new Date(Date.UTC(p.y, p.mo - 1, 1));
  if (locale.startsWith("en")) {
    const mon = dt.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    return `${mon} ${p.y}`;
  }
  // Chinese: assemble "七月 2026" — the combined native form is year-first
  // numeric ("2026年7月") regardless of month style.
  if (locale.startsWith("zh")) {
    const mon = dt.toLocaleDateString(locale, { month: "long", timeZone: "UTC" });
    return `${mon} ${p.y}`;
  }
  return dt.toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};
